import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Card, Steps, Upload, Button, Space, Alert, Input,
  Form, Typography, Divider, Checkbox, List,
} from 'antd';
import { InboxOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import { APIClient } from '../../helpers/api_helper';
import AccountSelect from '../../Common/AccountSelect';
import { importTargetLabel, intentFieldLabel } from '../../Common/intentFieldLabels';
import { useOrgConfig } from '../../Common/Hooks/OrgConfigHooks';
import { triggerErrorNotif, triggerNotification } from '../../slices/notifications/thunk';
import ColumnMapper from './ColumnMapper';
import {
  AMOUNT_FIELDS, JOIN_SEPARATOR, columnsOf, compactMapping, mergeWithProfile,
} from './columnMapping';

// Re-exported: it lived here before the mapping helpers had a module of their
// own, and the tests still import it from the wizard.
export { mergeWithProfile };

const api = new APIClient();

// Two picks of the same statement. Name alone would reject a genuine "Q1.csv"
// from a different bank folder, so size and mtime have to agree too.
const isSameFile = (f) => (other) => (
  other.name === f.name
  && other.size === f.size
  && other.lastModified === f.lastModified
);

const DATE_FORMAT_OPTS = [
  { value: '%d.%m.%Y', label: '31.12.2026' },
  { value: '%d. %m. %Y', label: '31. 12. 2026' },
  { value: '%Y-%m-%d', label: '2026-12-31' },
  { value: '%d/%m/%Y', label: '31/12/2026 (dan/mesec)' },
  { value: '%m/%d/%Y', label: '12/31/2026 (mesec/dan)' },
  { value: '%d.%m.%y', label: '31.12.26' },
];

/**
 * Upload a statement and say which column is what.
 *
 * Produces an attempt and hands its id back; it never saves a transaction --
 * everything it creates lands in the review screen first.
 */
const ImportWizard = ({ onCreated, onCancel }) => {
  const dispatch = useDispatch();
  const orgConfig = useOrgConfig();
  // The mapping screen names targets exactly as the drawer does, so the user
  // maps a column onto a word they already know.
  const label = (field) => importTargetLabel(field, orgConfig);

  const [step, setStep] = useState(0);
  // A list, not one file: a year is often exported one quarter at a time, and
  // the backend accepts them as a single import so long as the columns match.
  const [files, setFiles] = useState([]);
  const [account, setAccount] = useState(null);
  const [sniffed, setSniffed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [amountShape, setAmountShape] = useState('SIGNED');
  const [dateFormat, setDateFormat] = useState(null);
  const [decimalSep, setDecimalSep] = useState(',');
  const [profileName, setProfileName] = useState('');
  const [createMissingTags, setCreateMissingTags] = useState(false);
  // On by default: the rules are the org's own, written to be applied, and
  // they only touch rows the file left untagged.
  const [applyTagRules, setApplyTagRules] = useState(true);
  const [busy, setBusy] = useState(false);

  const doSniff = () => {
    if (!files.length || !account) return;
    const form = new FormData();
    // Same field name for every file: the server reads request.FILES.getlist.
    files.forEach(f => form.append('file', f));

    setBusy(true);
    // Content-Type must be cleared per request: api_helper sets a global
    // application/json default for POSTs, which would stop the browser writing
    // the multipart boundary.
    api.post('/api/statement_import/sniff/', form, {
      successMsg: null,
      headers: { 'Content-Type': undefined },
    })
      .then((resp) => {
        if (!resp) return;
        setSniffed(resp);
        const profile = resp.matched_profile;
        setMapping(mergeWithProfile(resp.suggested_mapping, profile?.mapping));
        setAmountShape(profile?.amount_shape || resp.amount_shape);
        setDateFormat(profile?.date_format || resp.date_format);
        setDecimalSep(profile?.decimal_separator || ',');
        setProfileName(profile?.title || '');
        setStep(1);
      })
      // The interceptor has already shown the reason -- mismatched columns is
      // now an everyday one. Swallowed so the rejection does not also raise
      // CRA's dev overlay on top of the message the user is meant to read.
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const doCommit = () => {
    setBusy(true);
    api.post('/api/statement_import/commit/', {
      token: sniffed.token,
      mapping: compactMapping(mapping),
      amount_shape: amountShape,
      date_format: dateFormat,
      decimal_separator: decimalSep,
      header_signature: sniffed.header_signature,
      account,
      save_profile_as: profileName || null,
      create_missing_tags: createMissingTags,
      apply_tag_rules: applyTagRules,
    }, { successMsg: 'Vrstice so pripravljene za pregled' })
      .then((resp) => {
        if (!resp?.batch?.id) return;
        // Creating master data is the one thing here that outlives the attempt:
        // deleting the batch would not delete these, so say what was made.
        if (resp.created_tags?.length) {
          dispatch(triggerNotification(
            `Nove oznake: ${resp.created_tags.join(', ')}`, 'info', 4000
          ));
        }
        onCreated(resp.batch.id);
      })
      .catch((err) => {
        if (err?.response?.data?.code === 'sniff_expired') {
          dispatch(triggerErrorNotif('Seja je potekla|Datoteko naložite znova.'));
          setStep(0);
          setSniffed(null);
        }
      })
      .finally(() => setBusy(false));
  };

  const visibleFields = (sniffed?.target_fields || [])
    .filter((f) => {
      if (!AMOUNT_FIELDS.includes(f.field)) return true;
      return amountShape === 'DEBIT_CREDIT'
        ? f.field !== 'amount'
        : f.field === 'amount';
    })
    .map((f) => ({ ...f, label: label(f.field) }));

  const isMapped = (field) => columnsOf(mapping[field]).length > 0;
  const missingRequired = visibleFields
    .filter(f => f.required && !isMapped(f.field))
    .map(f => f.label);
  const amountMapped = amountShape === 'DEBIT_CREDIT'
    ? (isMapped('debit') || isMapped('credit'))
    : isMapped('amount');

  return (
    <Card
      title="Nov uvoz"
      extra={<Button onClick={onCancel}>Prekliči</Button>}
    >
        <Steps
          current={step}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Naloži datoteko' },
            { title: 'Preveri stolpce' },
            { title: 'Potrdi' },
          ]}
        />

        {step === 0 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form layout="vertical">
              <Form.Item
                label={intentFieldLabel('account')}
                required
                help="Izpisek pripada enemu računu. Izberite katerega."
              >
                <AccountSelect value={account} onChange={setAccount} />
              </Form.Item>
            </Form>

            <Upload.Dragger
              accept=".csv,.xlsx,.xlsm"
              multiple
              // Returning false keeps antd from uploading; the files are held
              // here and posted together by doSniff.
              //
              // Deduplicated on identity, not on antd's uid: a uid is assigned
              // per pick, so choosing the same statement twice would pass the
              // uid check and import every one of its transactions twice --
              // silently, since both copies parse perfectly.
              beforeUpload={(f) => {
                setFiles(prev => (prev.some(isSameFile(f)) ? prev : [...prev, f]));
                return false;
              }}
              onRemove={(f) => setFiles(prev => prev.filter(x => x.uid !== f.uid))}
              fileList={files}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Kliknite ali povlecite izpiske sem</p>
              <p className="ant-upload-hint">
                Podprti sta datoteki CSV in XLSX (največ 10 MB). Naložite lahko več
                datotek hkrati, če imajo enake stolpce.
              </p>
            </Upload.Dragger>

            <Button
              type="primary" loading={busy}
              disabled={!files.length || !account}
              icon={<ArrowRightOutlined />}
              onClick={doSniff}
            >
              Naprej
            </Button>
          </Space>
        )}

        {step === 1 && sniffed && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {sniffed.matched_profile && (
              <Alert
                type="success" showIcon
                message={`Uporabljen shranjeni profil: ${sniffed.matched_profile.title}`}
                description="Stolpci so že nastavljeni. Preverite jih in nadaljujte."
              />
            )}
            {sniffed.date_format_ambiguous_with && (
              <Alert
                type="warning" showIcon
                message="Zapis datuma je dvoumen"
                description="Iz vzorca ni razvidno, ali je prvi del dan ali mesec. Izberite pravilnega spodaj."
              />
            )}

            <ColumnMapper
              fields={visibleFields}
              headers={sniffed.headers}
              sampleRows={sniffed.sample_rows}
              mapping={mapping}
              setMapping={setMapping}
              joinSeparator={sniffed.join_separator || JOIN_SEPARATOR}
              amountShape={amountShape}
              setAmountShape={setAmountShape}
              dateFormat={dateFormat}
              setDateFormat={setDateFormat}
              dateFormatOptions={DATE_FORMAT_OPTS}
              decimalSep={decimalSep}
              setDecimalSep={setDecimalSep}
            />

            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}>Nazaj</Button>
              <Button
                type="primary" icon={<ArrowRightOutlined />}
                disabled={!isMapped('date_to_be_paid') || !amountMapped}
                onClick={() => setStep(2)}
              >
                Naprej
              </Button>
            </Space>
            {missingRequired.length > 0 && (
              <Alert type="error" showIcon
                message={`Manjkajo obvezni stolpci: ${missingRequired.join(', ')}`} />
            )}
          </Space>
        )}

        {step === 2 && sniffed && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              type="info" showIcon
              message={`${sniffed.row_count} vrstic bo pripravljenih za pregled`}
              description="Transakcije še ne bodo shranjene. Pregledate in uredite jih na strani Osnutki, nato jih shranite."
            />

            {/* Only when there is more than one -- for a single file the name
                is already on the step before this one. */}
            {(sniffed.files || []).length > 1 && (
              <List
                size="small" bordered
                header={<Typography.Text strong>Datoteke ({sniffed.files.length})</Typography.Text>}
                dataSource={sniffed.files}
                style={{ maxWidth: 420 }}
                renderItem={(f) => (
                  <List.Item>
                    <span>{f.filename}</span>
                    <Typography.Text type="secondary">{f.row_count} vrstic</Typography.Text>
                  </List.Item>
                )}
              />
            )}

            <Form layout="vertical" style={{ maxWidth: 520 }}>
              <Divider orientation="left">{label('tag')}</Divider>
              <Form.Item>
                <Checkbox
                  checked={createMissingTags}
                  onChange={(e) => setCreateMissingTags(e.target.checked)}
                >
                  Ustvari nove oznake, če še ne obstajajo
                </Checkbox>
                <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0 24px' }}>
                  Brez tega se oznake iz datoteke le poiščejo med obstoječimi,
                  neznane pa ostanejo prazne.
                </Typography.Paragraph>
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={applyTagRules}
                  onChange={(e) => setApplyTagRules(e.target.checked)}
                >
                  Označi po obstoječih pravilih za oznake
                </Checkbox>
                <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0 24px' }}>
                  Vrstice brez oznake dobijo tisto iz prvega pravila, ki se ujema
                  z nazivom. Če ima pravilo nadomestni naziv, se naziv tudi
                  preimenuje.
                </Typography.Paragraph>
              </Form.Item>

              <Divider orientation="left">Profil</Divider>
              <Form.Item
                label="Shrani nastavitve stolpcev kot profil"
                help="Ob naslednjem uvozu iz iste banke bodo stolpci nastavljeni samodejno."
              >
                <Input
                  placeholder="npr. NLB CSV"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  maxLength={56}
                  style={{ maxWidth: 420 }}
                />
              </Form.Item>
            </Form>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(1)}>Nazaj</Button>
              <Button type="primary" loading={busy} onClick={doCommit}>
                Pripravi za pregled
              </Button>
            </Space>
          </Space>
        )}
      </Card>
  );
};

export default ImportWizard;
